<template>
  <div>
    <div v-if="loading" class="tw-py-6">
      <ProgressSpinner style="width:28px;height:28px" strokeWidth="4" />
    </div>
    <Message v-else-if="error" severity="error" :closable="false">
      {{ friendlyError(error) }}
      <Button
        label="Retry"
        icon="pi pi-refresh"
        class="p-button-text p-button-sm tw-ml-2"
        @click="load"
      />
    </Message>
    <slot v-else-if="stats" :stats="stats" />
  </div>
</template>

<script>
import gql from 'graphql-tag'

const SYNC_STATS = gql`
  fragment databaseRecordFields on DatabaseRecord {
    created_at
    updated_at
  }

  query SyncStats($sourceName: String) {
    syncStats(sourceName: $sourceName) {
      demoMode
      warehouseFeeds {
        sourceName
        destinationName
        feedSlug
        feedDisplayName
        feedStatus
        listingCount
        lastSuccessfulSyncAt
        error
      }
      sync {
        id
        name
        batch_id
        result
        error
        ...databaseRecordFields
        resources {
          id
          name
          is_done
          ...databaseRecordFields
          destinations {
            id
            name
            num_records_synced
            ...databaseRecordFields
          }
        }
      }
      purge {
        id
        name
        batch_id
        result
        error
        ...databaseRecordFields
        resources {
          id
          name
          is_done
          ...databaseRecordFields
          destinations {
            id
            name
            num_records_purged
            ids_purged
            ...databaseRecordFields
          }
        }
      }
      reconcile {
        id
        name
        batch_id
        result
        error
        ...databaseRecordFields
        resources {
          id
          name
          is_done
          ...databaseRecordFields
          destinations {
            id
            name
            num_records_reconciled
            ...databaseRecordFields
          }
        }
      }
    }
  }
`

export default {
  props: {
    sourceName: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      stats: null,
    }
  },
  watch: {
    sourceName: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    friendlyError(error) {
      const msg = (error && error.message) || 'An error occurred'
      if (/load failed|failed to fetch|network error|econnrefused/i.test(msg)) {
        return 'API offline — leave npm run dev running (auto-restarts). Retry in a moment.'
      }
      return msg
    },
    async load() {
      this.loading = true
      this.error = null
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1)
      try {
        const result = await Promise.race([
          this.$apollo.query({
            query: SYNC_STATS,
            variables: { sourceName: this.sourceName || null },
            fetchPolicy: 'no-cache',
            errorPolicy: 'none',
          }),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Dashboard stats timed out — is the API on :4000 running?')), 12000)
          }),
        ])
        if (seq !== this._loadSeq) return
        this.stats = result.data && result.data.syncStats
        if (!this.stats) {
          this.error = new Error('No sync stats returned')
        }
      } catch (err) {
        if (seq !== this._loadSeq) return
        this.error = err
        this.stats = null
      } finally {
        if (seq === this._loadSeq) this.loading = false
      }
    },
  },
}
</script>
